// ─────────────────────────────────────────────
//  LearnPage.jsx — VeeraStockIQ Learn Stock Market
//  Complete interactive learning experience
// ─────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import "./Learnpage.css";

// ── Curriculum Data ──────────────────────────────────────────────────────────
const LEVELS = [
  {
    id: 1, title: "Stock Market Basics", icon: "🏛️", xp: 100,
    color: "#22c55e", description: "What is the stock market and why it exists",
    lessons: [
      {
        id: "l1-1", title: "What is the Stock Market?",
        duration: "3 min", type: "concept",
        content: {
          headline: "Imagine a giant bazaar — but for pieces of companies",
          body: "The stock market is a place where people buy and sell tiny ownership pieces of companies. When a company wants to raise money, it breaks itself into millions of tiny pieces called 'shares' and sells them to anyone who wants to own a part of that company.",
          visual: "bazaar",
          analogy: {
            icon: "🏪", title: "The Chai Shop Analogy",
            text: "Imagine your friend opens a chai shop. She needs ₹1 lakh to start. Instead of taking a bank loan, she asks 10 friends to invest ₹10,000 each. Now each friend owns 10% of the shop. If the shop earns well, they earn well. That's exactly what stocks are."
          },
          keyPoints: [
            "The stock market is NOT gambling if you understand it",
            "Companies sell shares to raise money for growth",
            "Buying a share = owning a tiny piece of that company",
            "Your money grows when the company grows"
          ]
        },
        quiz: {
          question: "Your friend starts a company worth ₹10 lakhs and gives you 100 shares out of 1000 total shares. What percentage do you own?",
          options: ["5%", "10%", "1%", "100%"],
          correct: 1,
          explanation: "You own 100 out of 1000 shares = 100/1000 = 10% of the company! When the company grows, your 10% is worth more."
        }
      },
      {
        id: "l1-2", title: "What is NSE and BSE?",
        duration: "2 min", type: "concept",
        content: {
          headline: "India has two major stock exchanges",
          body: "Think of stock exchanges as marketplaces — like Amazon and Flipkart, but for buying and selling company shares. NSE (National Stock Exchange) and BSE (Bombay Stock Exchange) are India's two biggest exchanges.",
          visual: "exchanges",
          analogy: {
            icon: "🛒", title: "The Market Analogy",
            text: "NSE and BSE are like two big markets in your city. Most traders go to NSE because it's larger and more liquid (more buyers and sellers). BSE is older — it was started in 1875! NSE started in 1994 and quickly became more popular for trading."
          },
          keyPoints: [
            "NSE = National Stock Exchange (newer, more popular for trading)",
            "BSE = Bombay Stock Exchange (world's oldest in Asia, started 1875)",
            "NIFTY 50 = NSE's top 50 companies index",
            "SENSEX = BSE's top 30 companies index"
          ]
        },
        quiz: {
          question: "NIFTY 50 tracks the performance of...",
          options: ["50 random stocks", "Top 50 companies on NSE", "50 government bonds", "All stocks in India"],
          correct: 1,
          explanation: "NIFTY 50 is an index that represents the 50 largest and most actively traded companies on the NSE. When NIFTY goes up, it means most top companies are doing well."
        }
      },
      {
        id: "l1-3", title: "How Do Investors Make Money?",
        duration: "3 min", type: "concept",
        content: {
          headline: "Two ways to profit from stocks",
          body: "There are two primary ways you can make money from owning stocks: price appreciation (the stock goes up in value) and dividends (company shares its profits with you).",
          visual: "profit",
          analogy: {
            icon: "🌳", title: "The Mango Tree Analogy",
            text: "Imagine you buy a mango sapling for ₹100. As it grows, it becomes worth ₹500 (price appreciation). Plus, every year it gives you mangoes you can sell for ₹50 (dividends). Stocks work the same way — you profit from both growth AND income."
          },
          keyPoints: [
            "Capital Gains = You buy at ₹100, sell at ₹200 = ₹100 profit",
            "Dividends = Company shares profits with shareholders",
            "Long-term investors benefit from compounding",
            "Holding for 1+ year = lower tax (LTCG) in India"
          ]
        },
        quiz: {
          question: "You buy 100 shares of TCS at ₹3,500 each. After 2 years, the price is ₹4,200. What is your total profit?",
          options: ["₹70,000", "₹7,000", "₹700", "₹42,000"],
          correct: 0,
          explanation: "Profit per share = ₹4,200 - ₹3,500 = ₹700. Total profit = ₹700 × 100 shares = ₹70,000! That's a 20% return in 2 years."
        }
      }
    ]
  },
  {
    id: 2, title: "Demat & Trading Accounts", icon: "🏦", xp: 100,
    color: "#3b82f6", description: "How to actually buy and sell stocks",
    lessons: [
      {
        id: "l2-1", title: "What is a Demat Account?",
        duration: "3 min", type: "concept",
        content: {
          headline: "Your digital locker for shares",
          body: "In the old days, when you bought stocks, companies gave you a paper certificate. If it got lost or burned, your investment was gone! In 1996, India switched to electronic shares stored in a Demat account — short for Dematerialized account.",
          visual: "demat",
          analogy: {
            icon: "🏦", title: "The Bank Account Analogy",
            text: "Just like a bank account holds your money digitally, a Demat account holds your shares digitally. You open it with a Depository Participant (DP) like Zerodha, Groww, or Upstox. NSDL and CDSL are the two national depositories that actually store your shares safely."
          },
          keyPoints: [
            "Demat = stores your shares electronically (no paper)",
            "NSDL and CDSL are India's two depositories",
            "Zerodha, Groww, Upstox = popular Demat providers (DPs)",
            "Opening a Demat account requires PAN + Aadhaar"
          ]
        },
        quiz: {
          question: "What does 'Demat' stand for?",
          options: ["Deposit Maturity", "Dematerialized", "Digital Market", "Derivative Matrix"],
          correct: 1,
          explanation: "Demat stands for Dematerialized — meaning your physical share certificates have been converted into electronic form. No more paper certificates that can be lost!"
        }
      },
      {
        id: "l2-2", title: "Demat vs Trading Account",
        duration: "2 min", type: "comparison",
        content: {
          headline: "Two accounts, two different jobs",
          body: "Many beginners confuse these. They work together, but serve different purposes. Think of it like a warehouse and a shop — one stores, one sells.",
          visual: "comparison",
          analogy: {
            icon: "🏪", title: "Warehouse vs Shop",
            text: "Demat Account = Warehouse (stores your shares). Trading Account = Shop counter (where you place buy/sell orders). The broker connects them. When you buy shares, they first go to your Trading account, then settle into your Demat within T+1 day."
          },
          keyPoints: [
            "Demat Account = holds/stores your shares (like a locker)",
            "Trading Account = place buy/sell orders (like a gateway)",
            "Most brokers give both together when you sign up",
            "T+1 settlement = shares delivered next trading day"
          ]
        },
        quiz: {
          question: "You placed an order to buy Reliance shares on Monday. When will the shares appear in your Demat account?",
          options: ["Immediately", "Tuesday (T+1)", "Wednesday (T+2)", "Next week"],
          correct: 1,
          explanation: "India moved to T+1 settlement in 2023. This means shares bought on Monday (T) will be credited to your Demat by Tuesday (T+1). Previously it was T+2."
        }
      }
    ]
  },
  {
    id: 3, title: "Essential Terminology", icon: "📖", xp: 150,
    color: "#f59e0b", description: "The words every investor must know",
    lessons: [
      {
        id: "l3-1", title: "Market Cap Explained",
        duration: "3 min", type: "concept",
        content: {
          headline: "How big is a company? Market Cap tells you",
          body: "Market Capitalization (Market Cap) = Total number of shares × Current share price. It tells you the total market value of a company. It's like a price tag on the entire company.",
          visual: "marketcap",
          analogy: {
            icon: "⚖️", title: "The Weighing Scale",
            text: "If a company has 1 crore shares and each share costs ₹500, the Market Cap = ₹500 crore. A Large Cap company (₹20,000 crore+) is like a big established banyan tree — stable but slow growth. A Small Cap is like a sapling — risky but could grow 10x."
          },
          keyPoints: [
            "Large Cap: ₹20,000 crore+ (Reliance, TCS, Infosys)",
            "Mid Cap: ₹5,000–20,000 crore (medium-sized companies)",
            "Small Cap: Below ₹5,000 crore (smaller, riskier companies)",
            "Bigger market cap ≠ better stock. Price matters too."
          ]
        },
        quiz: {
          question: "A company has 50 lakh shares. Today's share price is ₹400. What is its Market Cap?",
          options: ["₹200 crore", "₹400 crore", "₹50 crore", "₹20 crore"],
          correct: 0,
          explanation: "Market Cap = 50,00,000 shares × ₹400 = ₹200,00,00,000 = ₹200 crore. Simple! Now you know why Reliance's market cap is over ₹15 lakh crore — it has billions of shares."
        }
      },
      {
        id: "l3-2", title: "Bull vs Bear Market",
        duration: "2 min", type: "concept",
        content: {
          headline: "When markets attack — differently",
          body: "A Bull Market is when stock prices are rising and investors are optimistic. A Bear Market is when prices are falling and investors are scared. Both are normal parts of the market cycle.",
          visual: "bullbear",
          analogy: {
            icon: "🐂", title: "How the Animals Attack",
            text: "A bull thrusts its horns UPWARD — representing rising prices. A bear swipes its claws DOWNWARD — representing falling prices. Easy to remember! India has experienced many bull markets (2003–2008, 2020–2021) and bear markets (2008 crash, 2020 COVID crash)."
          },
          keyPoints: [
            "Bull Market = prices rising 20%+ from recent lows",
            "Bear Market = prices falling 20%+ from recent highs",
            "Bear markets are temporary — historically markets always recover",
            "Best time to buy = during bear markets (but scary!)"
          ]
        },
        quiz: {
          question: "NIFTY 50 was at 18,000 in Jan. By June it fell to 14,000. What type of market is this?",
          options: ["Bull Market", "Bear Market", "Sideways Market", "Bull Trap"],
          correct: 1,
          explanation: "A fall of (18000-14000)/18000 = 22.2% qualifies as a Bear Market (typically defined as 20%+ decline). The COVID crash of March 2020 was a bear market — NIFTY fell ~38% in weeks!"
        }
      },
      {
        id: "l3-3", title: "Dividend Explained Simply",
        duration: "2 min", type: "concept",
        content: {
          headline: "Getting paid just for owning shares",
          body: "Some companies share a portion of their profits with shareholders as 'dividends'. You get paid just for holding the stock — even if you don't sell it.",
          visual: "dividend",
          analogy: {
            icon: "💰", title: "Rent from a Flat",
            text: "Imagine you own a flat. Every month you get rental income. Dividends are similar — you own shares, and the company pays you a share of its profits periodically. ITC, Coal India, BPCL are known for high dividend payouts in India."
          },
          keyPoints: [
            "Dividend Yield = Annual Dividend / Share Price × 100",
            "Ex-Dividend Date = you must hold shares BEFORE this date to get dividend",
            "High dividends are good, but don't ignore company growth",
            "Some growth companies (startups) pay zero dividends — they reinvest"
          ]
        },
        quiz: {
          question: "Coal India's share price is ₹200. It pays ₹20 annual dividend. What is the dividend yield?",
          options: ["5%", "10%", "20%", "1%"],
          correct: 1,
          explanation: "Dividend Yield = (₹20 / ₹200) × 100 = 10%. That's excellent! Many FDs give 6–7%. This is why dividend investors love Coal India — it's like a high-yield FD with bonus price appreciation."
        }
      }
    ]
  },
  {
    id: 4, title: "Fundamental Analysis", icon: "🔬", xp: 200,
    color: "#8b5cf6", description: "Read a company like an open book",
    lessons: [
      {
        id: "l4-1", title: "PE Ratio — Is a Stock Cheap or Expensive?",
        duration: "4 min", type: "concept",
        content: {
          headline: "The most famous valuation tool in the world",
          body: "PE Ratio (Price-to-Earnings Ratio) = Share Price / Earnings Per Share (EPS). It tells you how much you're paying for every ₹1 of the company's earnings. A lower PE can mean the stock is cheaper (but not always!).",
          visual: "pe",
          analogy: {
            icon: "🏪", title: "The Chai Shop PE",
            text: "If a chai shop earns ₹1 lakh per year and someone offers to sell it to you for ₹10 lakh, you're paying a PE of 10x. If another chai shop earns the same but is priced at ₹20 lakh, that's a PE of 20x — more expensive! Would you pay more for the same earnings? Only if you expect the second shop to grow much faster."
          },
          keyPoints: [
            "PE = Price ÷ EPS (Earnings Per Share)",
            "Low PE = potentially undervalued (but check why!)",
            "High PE = market expects high future growth",
            "Compare PE within same sector — don't compare bank PE to tech PE"
          ]
        },
        quiz: {
          question: "Infosys earns ₹60 EPS. Its current share price is ₹1,500. What is its PE ratio?",
          options: ["15", "25", "40", "60"],
          correct: 1,
          explanation: "PE = Price / EPS = 1500 / 60 = 25. This means you're paying 25x Infosys's annual earnings. The IT sector in India typically trades at 20-30x PE, so 25 is in the normal range for Infosys."
        }
      },
      {
        id: "l4-2", title: "Revenue vs Profit — What's the Difference?",
        duration: "3 min", type: "concept",
        content: {
          headline: "Revenue is vanity. Profit is sanity.",
          body: "Revenue = Total money coming in. Profit = What's left after all expenses. A company can have huge revenue but zero profit (like many startups). Always check profit, not just revenue.",
          visual: "revenue",
          analogy: {
            icon: "🍜", title: "The Restaurant Analogy",
            text: "A restaurant earns ₹10 lakh in sales (Revenue). But rent ₹2L + salaries ₹3L + ingredients ₹2.5L + utilities ₹0.5L = ₹8L expenses. Profit = ₹10L - ₹8L = ₹2L. Profit margin = 20%. Now, what if a competing restaurant earns ₹50L in revenue but spends ₹51L? Bankrupt!"
          },
          keyPoints: [
            "Revenue = gross income before any expenses",
            "Gross Profit = Revenue minus Cost of Goods Sold",
            "Net Profit = Revenue minus ALL expenses (final profit)",
            "Profit Margin = (Net Profit / Revenue) × 100"
          ]
        },
        quiz: {
          question: "Company A: Revenue ₹500 crore, Net Profit ₹50 crore. Company B: Revenue ₹200 crore, Net Profit ₹40 crore. Which has better profit margins?",
          options: ["Company A (10%)", "Company B (20%)", "Both are equal", "Cannot determine"],
          correct: 1,
          explanation: "Company A margin = 50/500 = 10%. Company B margin = 40/200 = 20%. Company B keeps 20 paise for every rupee it earns vs Company A's 10 paise. Despite lower revenue, Company B is more efficient!"
        }
      }
    ]
  },
  {
    id: 5, title: "Technical Analysis", icon: "📈", xp: 200,
    color: "#ec4899", description: "Read charts like a pro trader",
    lessons: [
      {
        id: "l5-1", title: "What is a Candlestick Chart?",
        duration: "4 min", type: "visual",
        content: {
          headline: "Every candle tells a story",
          body: "A candlestick shows 4 things about a stock in one time period: Open price, Close price, High price, and Low price. The 'body' is between open and close. The 'wicks' show high and low. Green candle = price went UP. Red candle = price went DOWN.",
          visual: "candle",
          analogy: {
            icon: "🕯️", title: "Reading a Single Candle",
            text: "Imagine HDFC Bank on Monday: Opens at ₹1,500. During the day it touches ₹1,550 (high) and ₹1,480 (low). Finally closes at ₹1,530. Since close > open, it's a GREEN candle. The body spans ₹1,500–₹1,530. The upper wick goes to ₹1,550. The lower wick goes to ₹1,480."
          },
          keyPoints: [
            "Green candle = closed HIGHER than it opened (bullish)",
            "Red candle = closed LOWER than it opened (bearish)",
            "Long wick = price was volatile but came back",
            "Big body = strong price movement that day"
          ]
        },
        quiz: {
          question: "A candlestick has: Open ₹200, Close ₹180, High ₹210, Low ₹175. What color is this candle?",
          options: ["Green", "Red", "Could be either", "Blue"],
          correct: 1,
          explanation: "Close (₹180) < Open (₹200) means the stock fell during this period. That's a RED (bearish) candle. The wick goes up to ₹210 (high) and down to ₹175 (low). The body spans ₹180–₹200."
        }
      },
      {
        id: "l5-2", title: "Support and Resistance",
        duration: "3 min", type: "visual",
        content: {
          headline: "The invisible walls of the stock market",
          body: "Support is a price level where a stock tends to stop falling and bounce back up. Resistance is a price level where a stock tends to stop rising and fall back down. These are the most powerful concepts in technical analysis.",
          visual: "sr",
          analogy: {
            icon: "🏀", title: "The Basketball Court Floor",
            text: "Support is like the floor — the ball keeps bouncing off it. Resistance is like the ceiling — the ball keeps hitting it and coming back down. When a stock breaks through resistance, that old resistance often becomes new support! This is called 'role reversal'."
          },
          keyPoints: [
            "Support = price floor (buyers step in here)",
            "Resistance = price ceiling (sellers step in here)",
            "More times price bounces = stronger the level",
            "Breakout = price breaks through resistance with high volume"
          ]
        },
        quiz: {
          question: "Reliance bounces off ₹2,400 three times over 6 months. Then it breaks above ₹2,400 with huge volume. What happens to ₹2,400 now?",
          options: ["Becomes stronger resistance", "Becomes support", "Becomes irrelevant", "Price will fall back to ₹2,000"],
          correct: 1,
          explanation: "Classic 'role reversal'! ₹2,400 was resistance 3 times. When price breaks above it decisively, that level flips to become support. Now bulls defend ₹2,400 instead of bears attacking it."
        }
      }
    ]
  },
  {
    id: 6, title: "Reading Financials", icon: "📊", xp: 250,
    color: "#14b8a6", description: "Decode P&L, Balance Sheet, Cash Flow",
    lessons: [
      {
        id: "l6-1", title: "Profit & Loss Statement",
        duration: "4 min", type: "concept",
        content: {
          headline: "The report card of a company",
          body: "The P&L (Profit and Loss) Statement shows how much money a company earned and spent over a period (quarterly or annually). It goes from Revenue at the top to Net Profit at the bottom — like a waterfall.",
          visual: "pl",
          analogy: {
            icon: "📋", title: "The P&L Waterfall",
            text: "Start with Total Revenue → subtract Cost of Goods Sold = Gross Profit. Subtract Operating Expenses = EBIT. Subtract Interest + Taxes = Net Profit. Each step removes something until you reach the final profit. This is why analysts call it the 'income statement waterfall'."
          },
          keyPoints: [
            "Revenue = all money coming in from sales",
            "EBITDA = earnings before interest, tax, depreciation",
            "PAT (Profit After Tax) = final bottom line profit",
            "Growing PAT + stable margins = healthy company"
          ]
        },
        quiz: {
          question: "A company's revenue is ₹1,000 crore. COGS = ₹400 crore, Operating expenses = ₹200 crore, Interest = ₹50 crore, Tax = ₹100 crore. What is Net Profit?",
          options: ["₹250 crore", "₹350 crore", "₹600 crore", "₹150 crore"],
          correct: 0,
          explanation: "Revenue ₹1000 - COGS ₹400 = Gross Profit ₹600. ₹600 - OpEx ₹200 = EBIT ₹400. ₹400 - Interest ₹50 = EBT ₹350. ₹350 - Tax ₹100 = Net Profit ₹250 crore. Step by step!"
        }
      }
    ]
  },
  {
    id: 7, title: "Risk Management", icon: "🛡️", xp: 150,
    color: "#f97316", description: "Protect your capital first",
    lessons: [
      {
        id: "l7-1", title: "The Golden Rule: Never Lose Big",
        duration: "3 min", type: "concept",
        content: {
          headline: "Warren Buffett's Rule #1 and Rule #2",
          body: "Rule #1: Never lose money. Rule #2: Never forget Rule #1. Protecting your capital is more important than making gains. A 50% loss requires a 100% gain just to break even!",
          visual: "risk",
          analogy: {
            icon: "🛡️", title: "The Hole in the Bucket",
            text: "If you have a bucket with 100 liters and you lose 50% (50 liters), you need to DOUBLE what's left (from 50 to 100) just to get back. Losing 10% requires only 11% to recover. Losing 90% requires 900% to recover! Small losses are manageable. Big losses destroy wealth."
          },
          keyPoints: [
            "Never put all money in one stock — diversify",
            "Use Stop Loss to limit downside automatically",
            "Never invest money you can't afford to lose",
            "Position sizing: risk only 1–2% of capital per trade"
          ]
        },
        quiz: {
          question: "You invest ₹1 lakh. The stock falls 50% to ₹50,000. By what % must it rise from ₹50,000 to get back to ₹1 lakh?",
          options: ["50%", "75%", "100%", "25%"],
          correct: 2,
          explanation: "₹50,000 needs to become ₹1,00,000. That's a DOUBLING = 100% gain needed just to break even! This is why avoiding large losses is so critical. A small gain every year beats one huge loss."
        }
      }
    ]
  },
  {
    id: 8, title: "Your First Stock Analysis", icon: "🔍", xp: 300,
    color: "#6366f1", description: "Put it all together on a real company",
    lessons: [
      {
        id: "l8-1", title: "5-Step Stock Analysis Framework",
        duration: "5 min", type: "framework",
        content: {
          headline: "A repeatable process to evaluate any stock",
          body: "Analyzing a stock isn't random. Follow these 5 steps every time: Business, Numbers, Valuation, Management, Risks.",
          visual: "framework",
          analogy: {
            icon: "🔎", title: "The Detective Framework",
            text: "Be a stock detective! Step 1: What does the company do? (Business). Step 2: Is it making money? (Numbers). Step 3: Is the stock price fair? (Valuation). Step 4: Is management trustworthy? (Management). Step 5: What could go wrong? (Risks). Now you're ready to use VeeraStockIQ's AI tools!"
          },
          keyPoints: [
            "Step 1 — Business: Understand what they sell and to whom",
            "Step 2 — Numbers: Revenue growth, profit margins, debt levels",
            "Step 3 — Valuation: Compare PE/PB to peers",
            "Step 4 — Management: Promoter holding, track record, governance"
          ]
        },
        quiz: {
          question: "Which is the BEST first step when analyzing an unknown company?",
          options: ["Check the PE ratio", "Understand what the business does", "Look at the chart", "Check the dividend yield"],
          correct: 1,
          explanation: "Always start with understanding the BUSINESS. If you don't understand how a company makes money, no amount of numbers analysis will help. Warren Buffett's first rule: invest only in businesses you understand."
        }
      }
    ]
  },
  // ── NEW LEVELS ────────────────────────────────────────────────────────────
  {
    id: 9, title: "Money & Inflation Basics", icon: "💵", xp: 120,
    color: "#10b981", description: "Why money loses value and how investing protects you",
    lessons: [
      {
        id: "l9-1", title: "What is Inflation?",
        duration: "3 min", type: "concept",
        content: {
          headline: "Why ₹100 today won't buy the same tomorrow",
          body: "Inflation is the rate at which prices rise over time. If inflation is 6%, something that costs ₹100 today will cost ₹106 next year. This means your money is silently losing purchasing power every single day you keep it idle.",
          visual: "inflation",
          analogy: {
            icon: "🛒", title: "The Grocery Basket",
            text: "In 2010, ₹500 filled a grocery basket. In 2024, that same basket costs ₹1,200+. Your money didn't change — but what it can buy shrank dramatically. That's inflation eating your wealth. India's average inflation is 5–7% per year."
          },
          keyPoints: [
            "Inflation = prices rising = money losing value",
            "India's CPI inflation averages 5–7% per year",
            "₹1 lakh kept in a drawer loses ~50% value in 10 years",
            "Investing > Saving to beat inflation"
          ]
        },
        quiz: {
          question: "You keep ₹1 lakh in a box for 10 years. Inflation is 7% per year. In real terms, your money's purchasing power becomes approximately...",
          options: ["₹1 lakh (unchanged)", "₹50,000 (halved)", "₹1.7 lakh (grown)", "₹10,000 (decimated)"],
          correct: 1,
          explanation: "At 7% inflation, purchasing power halves roughly every 10 years (Rule of 72: 72/7 = ~10 years). Your ₹1 lakh physically exists but can only buy what ₹50,000 could buy today. That's why idle cash is a guaranteed loss."
        }
      },
      {
        id: "l9-2", title: "Power of Compounding",
        duration: "4 min", type: "interactive",
        content: {
          headline: "The 8th wonder of the world",
          body: "Compounding means earning returns on your returns. Over time, this creates exponential growth. Einstein allegedly called it the 8th wonder of the world. Starting early matters far more than starting big.",
          visual: "compound",
          analogy: {
            icon: "❄️", title: "The Snowball on a Hill",
            text: "A small snowball rolling down a long hill picks up more snow each rotation. At first the growth seems tiny. But after rolling far enough, it's massive. Starting with ₹10,000 at age 22 vs ₹10,000 at age 32 can mean the difference of ₹10 lakh by retirement — same money, 10 years difference."
          },
          keyPoints: [
            "Compound interest = interest on interest",
            "Rule of 72: divide 72 by return rate to find doubling time",
            "At 12% returns: money doubles every 6 years",
            "Starting 10 years earlier can 3x your final wealth"
          ]
        },
        quiz: {
          question: "Using the Rule of 72: If your investment grows at 12% per year, approximately how many years to double your money?",
          options: ["12 years", "6 years", "8 years", "4 years"],
          correct: 1,
          explanation: "Rule of 72: 72 ÷ 12 = 6 years. At 12% annual returns, ₹1 lakh becomes ₹2 lakh in 6 years, ₹4 lakh in 12 years, ₹8 lakh in 18 years — without adding a single rupee! That's compounding magic."
        }
      },
      {
        id: "l9-3", title: "Saving vs Investing",
        duration: "3 min", type: "comparison",
        content: {
          headline: "Saving protects. Investing grows.",
          body: "Saving means putting money in low-risk places (FD, savings account) where it's safe but grows slowly — often less than inflation. Investing means putting money in assets (stocks, mutual funds) that can grow faster than inflation, with some risk.",
          visual: "saveinvest",
          analogy: {
            icon: "🌱", title: "Seed in a Box vs Seed in Soil",
            text: "Saving is like keeping a seed in a box — it's safe, it won't rot, but it won't grow either. Investing is like planting that seed in fertile soil — it could grow into a tree worth 100x the seed. The soil has some risk (drought, pests) but the long-term outcome is vastly better."
          },
          keyPoints: [
            "Savings account: 3–4% interest (below inflation)",
            "Fixed Deposit: 6–7% interest (barely beats inflation)",
            "Stock market: 12–15% average long-term returns",
            "Keep 6 months expenses in savings; invest the rest"
          ]
        },
        quiz: {
          question: "FD gives 6.5% interest. Inflation is 7%. Your real return after 1 year is:",
          options: ["+6.5%", "-0.5%", "+13.5%", "+0.5%"],
          correct: 1,
          explanation: "Real Return = Nominal Return − Inflation = 6.5% − 7% = −0.5%. You're actually LOSING purchasing power despite getting 'interest'. This is why investing is necessary, not optional, for long-term wealth."
        }
      }
    ]
  },
  {
    id: 10, title: "Investment Options", icon: "🧭", xp: 150,
    color: "#06b6d4", description: "Stocks, MFs, Gold, FDs — what suits you?",
    lessons: [
      {
        id: "l10-1", title: "Overview: All Investment Options",
        duration: "4 min", type: "comparison",
        content: {
          headline: "Not all investments are created equal",
          body: "There are many places to put your money. Each has different risk, return potential, and time horizon. Understanding all options helps you build a balanced portfolio — not just stocks.",
          visual: "investoptions",
          analogy: {
            icon: "🍽️", title: "The Investment Menu",
            text: "Think of investments like a restaurant menu. You don't eat only one dish. Stocks = spicy high-protein meal (high risk, high reward). FD = plain rice (safe, boring). Gold = a backup snack. Mutual Funds = a thali (diversified). A good investor orders wisely from the whole menu."
          },
          keyPoints: [
            "Equity (Stocks): 12–15% long-term, high risk",
            "Mutual Funds: 10–15%, diversified, managed",
            "Fixed Deposits: 6–7%, zero equity risk",
            "Gold: inflation hedge, 8–10% long-term"
          ]
        },
        quiz: {
          question: "You need money in 3 months for a medical emergency. Which is the SAFEST place to keep it?",
          options: ["Small-cap stocks", "Fixed Deposit / Liquid Fund", "Cryptocurrency", "Real Estate"],
          correct: 1,
          explanation: "Short-term money should NEVER go into volatile assets like stocks or crypto. FDs and liquid mutual funds give you safety + easy access. Stocks can fall 30–40% in 3 months. Never invest in equity what you need soon."
        }
      },
      {
        id: "l10-2", title: "What are Mutual Funds?",
        duration: "4 min", type: "concept",
        content: {
          headline: "Investing made easy — through professionals",
          body: "A mutual fund pools money from thousands of investors and invests it in stocks, bonds, or other assets — managed by a professional fund manager. You get diversification and professional expertise even with small amounts.",
          visual: "mutualfund",
          analogy: {
            icon: "🎯", title: "The Group Tour Analogy",
            text: "Going abroad alone is expensive and complicated. A group tour pools everyone's money, hires an expert guide, and everyone gets a great experience at lower cost. Mutual funds do exactly this for investing. You invest ₹500/month via SIP and get exposure to 50+ companies."
          },
          keyPoints: [
            "SIP = Systematic Investment Plan (monthly auto-invest)",
            "NAV = Net Asset Value = fund's price per unit",
            "Expense Ratio = annual fee charged (should be below 1%)",
            "Index Fund = tracks NIFTY/SENSEX, lowest cost option"
          ]
        },
        quiz: {
          question: "A mutual fund's NAV is ₹50. You invest ₹5,000. How many units do you get?",
          options: ["50 units", "100 units", "5,000 units", "500 units"],
          correct: 1,
          explanation: "Units = Investment ÷ NAV = ₹5,000 ÷ ₹50 = 100 units. If NAV grows to ₹70, your 100 units are worth ₹7,000 — a ₹2,000 profit. NAV works exactly like a stock price but for mutual funds."
        }
      },
      {
        id: "l10-3", title: "ETFs and Index Funds",
        duration: "3 min", type: "concept",
        content: {
          headline: "The most beginner-friendly investment",
          body: "An Index Fund or ETF (Exchange Traded Fund) simply tracks a market index like NIFTY 50. Instead of picking individual stocks, you own a tiny piece of ALL 50 top companies. Low cost. No guesswork. Beats most active funds over time.",
          visual: "etf",
          analogy: {
            icon: "🏆", title: "Betting on the Game, Not One Player",
            text: "Instead of betting on one cricket player to perform, you bet on the entire Indian team. When India wins (economy grows), you win. NIFTY 50 ETF is like owning a share of the top 50 Indian companies. Reliance, TCS, HDFC, Infosys — all in one click."
          },
          keyPoints: [
            "NIFTY 50 ETF: buy all 50 top Indian companies at once",
            "Expense ratio of 0.05–0.2% (very cheap)",
            "Historical NIFTY returns: ~12% CAGR over 20 years",
            "Best for beginners: start with NIFTY 50 Index Fund SIP"
          ]
        },
        quiz: {
          question: "NIFTY 50 Index Fund has an expense ratio of 0.1%. An active large-cap fund charges 1.5%. Over 20 years on ₹10 lakh, which is better for returns (all else equal)?",
          options: ["Active fund (more management = better returns)", "Index fund (lower cost = more of your money compounds)", "Both are exactly equal", "Depends on the fund manager"],
          correct: 1,
          explanation: "Over 20 years, 1.4% annual fee difference is enormous. On ₹10 lakh, that's ₹3–5 lakh lost to fees in the active fund! Research shows 80%+ of active funds underperform index funds over 15+ years. Lower costs compound into significantly higher returns."
        }
      }
    ]
  },
  {
    id: 11, title: "Market Participants", icon: "👥", xp: 120,
    color: "#a855f7", description: "Who moves markets and how money flows",
    lessons: [
      {
        id: "l11-1", title: "FIIs vs DIIs — The Big Players",
        duration: "3 min", type: "concept",
        content: {
          headline: "Two giants that move Indian markets",
          body: "FIIs (Foreign Institutional Investors) are large foreign funds — like BlackRock, Vanguard — that invest in Indian markets. DIIs (Domestic Institutional Investors) are Indian institutions like LIC, mutual funds, and insurance companies. Their buying and selling drives major market moves.",
          visual: "fiidii",
          analogy: {
            icon: "⚖️", title: "The Tug of War",
            text: "FIIs and DIIs are in a constant tug of war. When FIIs pull money out (sell), markets fall. When DIIs buy into the dip, they stabilize. During COVID crash (2020), FIIs sold ₹70,000 crore, DIIs bought ₹55,000 crore — DIIs cushioned the fall."
          },
          keyPoints: [
            "FII net buying = generally bullish signal",
            "FII net selling = market may fall (watch for DIIs countering)",
            "VeeraStockIQ shows FII/DII data in real-time",
            "Retail investors are smallest — institutions move markets"
          ]
        },
        quiz: {
          question: "FIIs sold ₹20,000 crore in Indian stocks this month. What is the most likely market impact?",
          options: ["Markets will definitely rise", "Markets will likely fall (unless DIIs absorb)", "No impact on markets", "Only mid-caps will be affected"],
          correct: 1,
          explanation: "FII selling of ₹20,000 crore is significant. Markets typically fall when FIIs are large net sellers. However, if DIIs step in to buy that amount, the fall can be limited. Both FII + DII data together tell a more complete story."
        }
      },
      {
        id: "l11-2", title: "SEBI — The Market Regulator",
        duration: "2 min", type: "concept",
        content: {
          headline: "The referee of the stock market",
          body: "SEBI (Securities and Exchange Board of India) was established in 1992. It regulates all market participants — exchanges, brokers, listed companies, mutual funds — to protect investors and ensure fair markets.",
          visual: "sebi",
          analogy: {
            icon: "⚖️", title: "Cricket's Umpire",
            text: "In cricket, the umpire ensures fair play — no cheating, no manipulation. SEBI is that umpire for the stock market. It sets rules, investigates manipulation, punishes fraud, and forces companies to disclose information fairly. Without SEBI, markets would be the Wild West."
          },
          keyPoints: [
            "SEBI = Securities and Exchange Board of India",
            "Protects retail investors from fraud and manipulation",
            "Forces companies to disclose quarterly results",
            "Regulates IPOs, mutual funds, brokers, and exchanges"
          ]
        },
        quiz: {
          question: "A company releases fake financial results to pump its stock price. Which body investigates this?",
          options: ["RBI", "SEBI", "NSE", "Ministry of Finance"],
          correct: 1,
          explanation: "This is securities fraud — SEBI's jurisdiction. SEBI can investigate, penalize, and ban individuals/companies found guilty of market manipulation. Famous cases: Satyam scam (2009), NSEL scam (2013) — all investigated by SEBI."
        }
      }
    ]
  },
  {
    id: 12, title: "Advanced Fundamental Analysis", icon: "🔭", xp: 250,
    color: "#7c3aed", description: "ROE, ROCE, Debt, Cash Flow — complete toolkit",
    lessons: [
      {
        id: "l12-1", title: "ROE and ROCE",
        duration: "4 min", type: "concept",
        content: {
          headline: "How efficiently is the company using money?",
          body: "ROE (Return on Equity) measures how much profit a company generates per rupee of shareholders' equity. ROCE (Return on Capital Employed) includes debt too, giving a fuller picture. Both show management efficiency.",
          visual: "roe",
          analogy: {
            icon: "🏭", title: "The Factory Efficiency Test",
            text: "If a factory owner invests ₹1 crore and earns ₹15 lakh profit, ROE = 15%. A neighbor invests ₹1 crore and earns ₹8 lakh — ROE = 8%. Both have factories, but the first owner is far more efficient. High ROE (15%+) consistently over years = quality business."
          },
          keyPoints: [
            "ROE = Net Profit ÷ Shareholders' Equity × 100",
            "ROE > 15% is generally good for Indian companies",
            "ROCE includes debt in the denominator (better for capex-heavy sectors)",
            "Rising ROE over years = improving business quality"
          ]
        },
        quiz: {
          question: "Company X has ₹500 crore equity and ₹75 crore net profit. Company Y has ₹200 crore equity and ₹40 crore net profit. Which has better ROE?",
          options: ["Company X (15%)", "Company Y (20%)", "Both equal", "Cannot determine"],
          correct: 1,
          explanation: "ROE X = 75/500 = 15%. ROE Y = 40/200 = 20%. Company Y generates more profit per rupee of shareholder money — it's more efficient despite being smaller. Higher ROE = better capital allocation by management."
        }
      },
      {
        id: "l12-2", title: "Debt-to-Equity Ratio",
        duration: "3 min", type: "concept",
        content: {
          headline: "How much debt is too much?",
          body: "Debt-to-Equity (D/E) ratio = Total Debt ÷ Total Equity. It shows how much a company relies on borrowed money vs its own funds. High debt magnifies both profits AND losses — and can kill a company in a downturn.",
          visual: "debt",
          analogy: {
            icon: "🎲", title: "The Home Loan Analogy",
            text: "If you buy a ₹1 crore house with ₹20 lakh down and ₹80 lakh loan, your D/E = 4x (very high!). If property prices fall 25%, your equity is wiped out but the debt remains. Companies work the same. Tata Motors once had D/E of 7x — near-bankruptcy risk. D/E below 1 is comfortable."
          },
          keyPoints: [
            "D/E below 1 = more equity than debt (comfortable)",
            "D/E above 3 = high leverage, risky in downturns",
            "Capital-intensive sectors (infra, metals) naturally have higher D/E",
            "Reducing D/E over years = management strengthening balance sheet"
          ]
        },
        quiz: {
          question: "Company A: Equity ₹200 crore, Debt ₹400 crore. Company B: Equity ₹300 crore, Debt ₹150 crore. Which is financially safer?",
          options: ["Company A (D/E 2.0)", "Company B (D/E 0.5)", "Both are safe", "Cannot determine"],
          correct: 1,
          explanation: "Company A D/E = 400/200 = 2.0 (concerning). Company B D/E = 150/300 = 0.5 (comfortable). Company B has far less debt burden. If earnings fall during a recession, Company A may struggle to pay interest — Company B can weather it easily."
        }
      },
      {
        id: "l12-3", title: "Cash Flow vs Profit",
        duration: "4 min", type: "concept",
        content: {
          headline: "A company can show profit but be bankrupt",
          body: "Profit on paper (P&L) and actual cash in the bank (Cash Flow) can be very different due to accounting adjustments. Free Cash Flow (FCF) = Operating Cash Flow minus Capital Expenditure. FCF is real money the company generated and can use freely.",
          visual: "cashflow",
          analogy: {
            icon: "💧", title: "The Water Tank",
            text: "Profit is like rain falling. Cash flow is the water actually entering your tank. Some rain evaporates (non-cash items), some is redirected (capex). A business must collect water faster than it leaks out. Companies with high profit but negative cash flow are often faking it — look for this red flag!"
          },
          keyPoints: [
            "Operating Cash Flow = cash from core business operations",
            "Free Cash Flow = Operating CF − Capital Expenditure",
            "FCF-positive companies can pay dividends, buy back shares",
            "Profit can be manipulated; cash flow is harder to fake"
          ]
        },
        quiz: {
          question: "Company shows ₹100 crore net profit but −₹50 crore operating cash flow for 3 consecutive years. This is:",
          options: ["Excellent — high profit!", "A major red flag — profit may be fake", "Normal for growth companies", "Means they pay high dividends"],
          correct: 1,
          explanation: "Persistent negative operating cash flow with positive reported profit is a serious red flag. This pattern often indicates channel stuffing, fake sales, or aggressive accounting. Several Indian company frauds (IL&FS, DHFL) showed this pattern for years before collapse."
        }
      },
      {
        id: "l12-4", title: "PB Ratio and Book Value",
        duration: "3 min", type: "concept",
        content: {
          headline: "What does the company own vs what market pays?",
          body: "Book Value = Total Assets − Total Liabilities. It's what shareholders would get if the company were liquidated today. PB Ratio = Share Price ÷ Book Value Per Share. A PB below 1 can mean the stock trades cheaper than its assets.",
          visual: "pb",
          analogy: {
            icon: "🏠", title: "The Real Estate Analogy",
            text: "A house has a 'book value' based on construction cost (say ₹50 lakh). If you can buy it for ₹40 lakh (PB = 0.8), you're buying below asset value — potential bargain! But check why it's cheap. Banking stocks normally trade at 1–3x book. Great banks (HDFC) trade at 4–5x book because the market trusts their future."
          },
          keyPoints: [
            "PB < 1 = stock trades below asset value (potential value buy)",
            "PB > 5 = market prices in strong future growth",
            "Banking sector: compare PB to peers (sector-specific)",
            "Low PB alone is not enough — check ROE too"
          ]
        },
        quiz: {
          question: "Bank A trades at PB 0.7 (below book). Bank B trades at PB 3.5 (above book). Which is automatically the better buy?",
          options: ["Bank A (cheaper)", "Bank B (premium quality)", "Neither — need more data", "Always buy below book value"],
          correct: 2,
          explanation: "PB 0.7 might mean the bank has bad loans and deteriorating assets — cheap for a reason! PB 3.5 might mean it's a quality franchise with high ROE justifying premium. You need to check NPA levels, ROE, and growth. Low PB is a starting point, not a conclusion."
        }
      }
    ]
  },
  {
    id: 13, title: "Financial Statements Deep Dive", icon: "📋", xp: 250,
    color: "#0ea5e9", description: "Balance Sheet, Cash Flow, and red flags",
    lessons: [
      {
        id: "l13-1", title: "Balance Sheet Explained",
        duration: "4 min", type: "concept",
        content: {
          headline: "A company's financial health in one page",
          body: "The Balance Sheet shows what a company OWNS (Assets) and what it OWES (Liabilities) at a specific date. The difference = Equity (shareholders' ownership). The fundamental equation: Assets = Liabilities + Equity.",
          visual: "balancesheet",
          analogy: {
            icon: "⚖️", title: "Your Personal Balance Sheet",
            text: "You own: ₹5L savings, ₹30L house, ₹2L car = ₹37L assets. You owe: ₹15L home loan, ₹1L credit card = ₹16L liabilities. Your net worth (equity) = ₹37L − ₹16L = ₹21L. Companies work identically. A strong balance sheet = more assets than liabilities, with growing equity."
          },
          keyPoints: [
            "Current Assets = cash, receivables, inventory (short-term)",
            "Fixed Assets = land, machinery, property (long-term)",
            "Current Liabilities = payables due within 1 year",
            "Equity = what shareholders actually own"
          ]
        },
        quiz: {
          question: "A company has Total Assets ₹800 crore and Total Liabilities ₹500 crore. What is Shareholders' Equity?",
          options: ["₹1,300 crore", "₹300 crore", "₹500 crore", "₹800 crore"],
          correct: 1,
          explanation: "Equity = Assets − Liabilities = ₹800 − ₹500 = ₹300 crore. This ₹300 crore belongs to shareholders. If the company were liquidated today (at book value), shareholders would get ₹300 crore. Simple but powerful equation."
        }
      },
      {
        id: "l13-2", title: "Warning Signs in Financials",
        duration: "4 min", type: "concept",
        content: {
          headline: "Red flags that save you from bad stocks",
          body: "Knowing what's wrong is as valuable as knowing what's right. Certain financial patterns consistently predict trouble — from Satyam (2009) to YES Bank (2020). Spotting these early protects your money.",
          visual: "redflags",
          analogy: {
            icon: "🚨", title: "The Doctor's Warning Signs",
            text: "A doctor spots early disease through symptoms. A good investor spots financial disease through patterns: consistently falling promoter holding, rising debt every quarter, profit with negative cash flow, frequent 'exceptional items' that boost profit. Each is a symptom. Multiple symptoms together = serious illness."
          },
          keyPoints: [
            "Promoter pledging shares = promoter is desperate for cash",
            "Revenue growing but receivables growing faster = fake sales",
            "Profit up but cash flow negative for 3+ years = accounting tricks",
            "Frequent auditor changes = something to hide"
          ]
        },
        quiz: {
          question: "A company's promoter holding fell from 72% to 45% over 2 years while the stock was rising. This suggests:",
          options: ["Promoters are buying more stock", "Promoters may be selling into the rally — caution!", "This is normal portfolio rebalancing", "Company is buying back shares"],
          correct: 1,
          explanation: "Promoters selling their own company's stock while prices are high is a major red flag. They know the company best — if they're exiting, why should you be entering? Famous example: Promoters of several small-caps sold at peaks before those stocks crashed 80–90%."
        }
      }
    ]
  },
  {
    id: 14, title: "Technical Analysis Expanded", icon: "📉", xp: 250,
    color: "#f43f5e", description: "Moving averages, RSI, MACD, chart patterns",
    lessons: [
      {
        id: "l14-1", title: "Moving Averages: SMA & EMA",
        duration: "4 min", type: "visual",
        content: {
          headline: "Smoothing out the noise to see the trend",
          body: "A Moving Average calculates the average price over a specific period. SMA (Simple Moving Average) weights all periods equally. EMA (Exponential Moving Average) gives more weight to recent prices. The 50-day and 200-day MAs are widely followed.",
          visual: "ma",
          analogy: {
            icon: "📊", title: "The Temperature Average",
            text: "Instead of looking at today's temperature alone, you look at the 30-day average to understand if it's a hot or cold month. Moving averages do the same for stocks — they filter out daily noise and show the underlying direction. Price above 200-day MA = long-term uptrend."
          },
          keyPoints: [
            "Price above 50-day MA = short-term uptrend",
            "Price above 200-day MA = long-term bullish",
            "EMA reacts faster to price changes than SMA",
            "Golden Cross = 50-day MA crosses above 200-day MA (bullish)"
          ]
        },
        quiz: {
          question: "A stock's price crosses above its 200-day Moving Average for the first time in 8 months. This signal is typically:",
          options: ["Bearish — sell immediately", "Bullish — potential trend change to upside", "Neutral — no significance", "Only relevant for day traders"],
          correct: 1,
          explanation: "Breaking above the 200-day MA after a prolonged period below it is a significant bullish signal — called a 'regime change'. Many institutional investors use this as a trigger to start buying. It doesn't mean the stock will always go up, but historical data shows higher probability of gains."
        }
      },
      {
        id: "l14-2", title: "RSI — Overbought & Oversold",
        duration: "3 min", type: "visual",
        content: {
          headline: "Is the stock running too hot or too cold?",
          body: "RSI (Relative Strength Index) is a momentum indicator ranging from 0–100. Above 70 = overbought (may pull back). Below 30 = oversold (may bounce). It helps identify potential turning points, but works best with other indicators.",
          visual: "rsi",
          analogy: {
            icon: "🌡️", title: "The Fever Thermometer",
            text: "RSI is like a body temperature gauge. Normal = 40–60 (healthy trend). RSI 75 = running a fever (overbought, possible pullback). RSI 25 = hypothermia (oversold, possible bounce). But a fever doesn't always mean you'll feel better tomorrow — confirm with other signals."
          },
          keyPoints: [
            "RSI > 70 = overbought (don't blindly buy)",
            "RSI < 30 = oversold (potential buying opportunity)",
            "RSI divergence = powerful signal (price makes new high, RSI doesn't)",
            "In strong uptrends, RSI can stay above 70 for weeks"
          ]
        },
        quiz: {
          question: "A stock's RSI is at 28 while the overall market is in a strong uptrend. This suggests:",
          options: ["Sell immediately — very bearish", "Potential buying opportunity — stock may be oversold", "Market is about to crash", "Only relevant for intraday traders"],
          correct: 1,
          explanation: "RSI below 30 in an otherwise healthy market often signals that the stock has been oversold — perhaps due to temporary bad news. This can be a buying opportunity for contrarian investors. However, always confirm with price action and other indicators — RSI alone is not enough."
        }
      },
      {
        id: "l14-3", title: "MACD — Momentum Made Simple",
        duration: "3 min", type: "visual",
        content: {
          headline: "When two moving averages tell a story",
          body: "MACD (Moving Average Convergence Divergence) = 12-day EMA minus 26-day EMA. A 9-day EMA of MACD is the 'Signal Line'. When MACD crosses above the signal line = bullish. When it crosses below = bearish. The histogram shows the gap between them.",
          visual: "macd",
          analogy: {
            icon: "🚂", title: "Two Trains on a Track",
            text: "Imagine two trains (fast 12-day EMA and slow 26-day EMA) running on the same track. When the fast train pulls ahead of the slow one (MACD positive), momentum is bullish. When slow train catches up and overtakes (MACD negative), momentum is bearish. The gap between them = the histogram bars."
          },
          keyPoints: [
            "MACD above 0 = bullish momentum",
            "MACD crossover above signal line = buy signal",
            "MACD crossover below signal line = sell signal",
            "MACD divergence from price = powerful reversal warning"
          ]
        },
        quiz: {
          question: "MACD line (currently -2) crosses above the Signal line (-1.5). What does this indicate?",
          options: ["Strong sell signal", "Bullish momentum building (but still in negative zone)", "No significance", "Price will definitely rise 10% tomorrow"],
          correct: 1,
          explanation: "A MACD crossover above the signal line is bullish — it signals improving momentum. However, since MACD is still negative (-2), it means the stock is still in a longer downtrend but momentum is shifting. This is an early signal, not a guarantee. Wait for MACD to go above zero for stronger confirmation."
        }
      },
      {
        id: "l14-4", title: "Volume Analysis",
        duration: "3 min", type: "visual",
        content: {
          headline: "Price tells you what. Volume tells you why.",
          body: "Volume is the number of shares traded in a given period. High volume confirms price moves. Low volume makes price moves suspicious. A breakout with high volume is valid. The same breakout with low volume often fails.",
          visual: "volume",
          analogy: {
            icon: "🗣️", title: "The Crowd at a Game",
            text: "If 50,000 people celebrate a goal, it's a real achievement. If only 100 people clap, it's questionable. Volume is like crowd enthusiasm for a price move. Reliance breaking above ₹2,500 with 5 crore shares traded confirms the breakout. The same breakout with 50 lakh shares is weak — might be a fake-out."
          },
          keyPoints: [
            "High volume + price breakout = strong, confirmed move",
            "Low volume + price breakout = weak, often fails",
            "Falling price on low volume = weak selling (may stabilize)",
            "Rising price on declining volume = rally losing strength"
          ]
        },
        quiz: {
          question: "Infosys breaks out above a 6-month resistance of ₹1,600 with volume 3x its average. This is:",
          options: ["Fake breakout — ignore it", "Strong confirmed breakout — bullish signal", "Too risky to interpret", "Bearish divergence"],
          correct: 1,
          explanation: "Volume 3x average on a breakout = strong institutional participation. This is a textbook high-quality breakout. Large funds are buying, confirming the price move is genuine. This type of breakout has historically led to 10–20% gains in the following weeks in many studies."
        }
      }
    ]
  },
  {
    id: 15, title: "Trading Basics", icon: "⚡", xp: 200,
    color: "#f59e0b", description: "Order types, delivery vs intraday, settlement",
    lessons: [
      {
        id: "l15-1", title: "Delivery vs Intraday Trading",
        duration: "3 min", type: "comparison",
        content: {
          headline: "Two completely different approaches",
          body: "Delivery trading means buying shares and holding them (goes to your Demat). Intraday trading means buying and selling on the SAME DAY — positions must be closed before market close at 3:30 PM. Never take intraday positions home overnight.",
          visual: "trading",
          analogy: {
            icon: "🏪", title: "Shopkeeper vs Day Trader",
            text: "A shopkeeper (delivery investor) buys inventory, holds it, and sells when prices are right — sometimes months later. A day trader is like a vegetable vendor — buys in the morning market, sells the same evening. One builds wealth slowly; the other bets on daily price swings. Beginners: start with delivery only."
          },
          keyPoints: [
            "CNC (Cash and Carry) = delivery trade, shares go to Demat",
            "MIS (Margin Intraday Square-off) = intraday, auto-closes at 3:20 PM",
            "Intraday taxes are higher (short-term capital gains)",
            "80% of intraday traders lose money — start with delivery"
          ]
        },
        quiz: {
          question: "You bought 100 shares of Wipro using MIS order at ₹500. At 3:25 PM, you forgot to sell. What happens?",
          options: ["Shares go to your Demat automatically", "Broker auto-squares off the position at market price", "Position held till next day", "Trade is cancelled"],
          correct: 1,
          explanation: "MIS orders are intraday only. If not squared off, the broker's system auto-closes them at market price around 3:20–3:25 PM. This can result in a loss if price moved against you. Always set alerts for MIS positions — never forget!"
        }
      },
      {
        id: "l15-2", title: "Order Types Explained",
        duration: "4 min", type: "concept",
        content: {
          headline: "Market, Limit, Stop Loss — your three essential orders",
          body: "Understanding order types is fundamental. A market order executes immediately at current price. A limit order executes only at your specified price or better. A stop-loss order automatically sells if price falls below a set level — your safety net.",
          visual: "orders",
          analogy: {
            icon: "🛍️", title: "Shopping Analogies",
            text: "Market Order = walk into store, buy at whatever price it costs today. Limit Order = 'I'll only buy if the price drops to ₹X' (like waiting for a sale). Stop Loss = 'If this stock falls below ₹X, automatically sell' (like an insurance policy). Every investor must use stop losses."
          },
          keyPoints: [
            "Market Order: instant execution, price not guaranteed",
            "Limit Order: executes only at your price (or better)",
            "Stop Loss: protects from large losses — ALWAYS use it",
            "GTT (Good Till Triggered): limit order valid for 1 year on some platforms"
          ]
        },
        quiz: {
          question: "You own TCS shares at ₹3,800. You place a Stop Loss order at ₹3,600. TCS falls to ₹3,550. What happens?",
          options: ["Order waits till price goes back to ₹3,600", "Your shares are sold at market price (~₹3,550)", "Nothing — stop loss only works for buying", "Broker calls you for permission"],
          correct: 1,
          explanation: "A Stop Loss triggers when price hits ₹3,600, and your shares are sold at the next available market price (~₹3,550 in this case). This limits your loss to ₹200/share (from ₹3,800) rather than letting it fall further. Stop losses are non-negotiable risk management tools."
        }
      }
    ]
  },
  {
    id: 16, title: "Risk Management Deep Dive", icon: "🛡️", xp: 200,
    color: "#ef4444", description: "Position sizing, risk-reward, drawdown",
    lessons: [
      {
        id: "l16-1", title: "Risk-Reward Ratio",
        duration: "3 min", type: "concept",
        content: {
          headline: "Only take trades where the reward justifies the risk",
          body: "Risk-Reward Ratio = Potential Loss / Potential Gain. A 1:3 risk-reward means you risk ₹1 to make ₹3. Successful traders never take trades with less than 1:2 risk-reward. Even with 40% win rate, a 1:3 R:R strategy is profitable long-term.",
          visual: "rr",
          analogy: {
            icon: "🎲", title: "The Coin Flip Game",
            text: "Would you play a game where you risk ₹100 and can win ₹300? Even if you lose 6 out of 10 times: 4 wins × ₹300 = ₹1,200 profit. 6 losses × ₹100 = ₹600 loss. Net: +₹600 profit! Good traders don't need to be right often — they need good risk-reward ratios."
          },
          keyPoints: [
            "Minimum acceptable R:R ratio = 1:2 (risk ₹1, target ₹2)",
            "Calculate before entering ANY trade",
            "Entry, target, and stop loss — set all three before buying",
            "Bad R:R = gambling. Good R:R = systematic investing."
          ]
        },
        quiz: {
          question: "Stock at ₹100. Your stop loss: ₹90 (risk ₹10). Your target: ₹125 (reward ₹25). What is your R:R ratio?",
          options: ["1:1", "1:2.5", "2:1", "1:10"],
          correct: 1,
          explanation: "R:R = Risk/Reward = ₹10 / ₹25 = 1:2.5. This means for every ₹10 you risk, you stand to make ₹25. That's a good trade setup! With this R:R, you only need to win 30% of trades to be profitable overall."
        }
      },
      {
        id: "l16-2", title: "Position Sizing — Never Risk Too Much",
        duration: "3 min", type: "concept",
        content: {
          headline: "The most underrated skill in investing",
          body: "Position sizing determines how much of your capital to put in a single trade. The 2% rule: never risk more than 2% of your total capital on any single trade. This ensures no single loss can damage your portfolio seriously.",
          visual: "possize",
          analogy: {
            icon: "🥚", title: "Eggs in Different Baskets",
            text: "If you put all 12 eggs in one basket and drop it — disaster. Put 1 egg per basket — drop one basket, you still have 11 eggs. Position sizing is this for money. If you have ₹10 lakh and risk 2% = ₹20,000 max per trade. Even 10 consecutive losses only cost ₹2 lakh, not your entire portfolio."
          },
          keyPoints: [
            "2% Rule: maximum risk ₹20,000 on ₹10 lakh portfolio",
            "Position size = (2% × Portfolio) ÷ Distance to Stop Loss",
            "Never 'double down' on losing trades (averaging down)",
            "Survive first. Thrive second."
          ]
        },
        quiz: {
          question: "Portfolio = ₹5 lakh. You follow the 2% rule. Maximum risk per trade is:",
          options: ["₹2,000", "₹10,000", "₹50,000", "₹1,00,000"],
          correct: 1,
          explanation: "2% of ₹5,00,000 = ₹10,000 maximum risk per trade. If your stop loss is ₹50 away from entry, you can buy 200 shares (₹10,000 ÷ ₹50). This systematic approach prevents any single bad trade from destroying your capital."
        }
      }
    ]
  },
  {
    id: 17, title: "Investor Psychology", icon: "🧠", xp: 200,
    color: "#8b5cf6", description: "The mental game that separates winners from losers",
    lessons: [
      {
        id: "l17-1", title: "FOMO & Fear — Your Worst Enemies",
        duration: "3 min", type: "concept",
        content: {
          headline: "Emotions are the biggest portfolio killers",
          body: "FOMO (Fear Of Missing Out) makes investors buy at tops. Fear makes them sell at bottoms. These two emotions cause retail investors to consistently buy high and sell low — the exact opposite of what creates wealth.",
          visual: "fomo",
          analogy: {
            icon: "🎢", title: "The Rollercoaster of Emotions",
            text: "When a stock rises from ₹100 to ₹300, FOMO sets in — 'I'm missing this rally!' People buy at ₹300. Then it falls to ₹200 — fear kicks in, they sell at loss. Then it rises to ₹500. They missed the real gain because emotions drove decisions, not analysis."
          },
          keyPoints: [
            "Never buy a stock just because it's 'going up fast'",
            "Never sell a quality stock just because it fell 10–15%",
            "Invest based on analysis, not social media tips",
            "If you can't explain WHY you own a stock, you shouldn't own it"
          ]
        },
        quiz: {
          question: "Your neighbour made 3x returns on a penny stock. You feel a strong urge to invest in similar penny stocks. This is:",
          options: ["Smart investing — follow the winners!", "FOMO bias — dangerous territory", "Due diligence — research the stock", "Value investing"],
          correct: 1,
          explanation: "This is classic FOMO bias. You're responding to your neighbour's success story, not to analysis. For every neighbour who made 3x, 99 others lost 80–90% in penny stocks. Never invest based on others' wins — investigate the full story, including all the losses you didn't hear about."
        }
      },
      {
        id: "l17-2", title: "Confirmation Bias & Anchoring",
        duration: "3 min", type: "concept",
        content: {
          headline: "Your brain actively lies to you about investments",
          body: "Confirmation bias: you seek information that confirms what you already believe. Anchoring: you fixate on a specific price (like the price you paid) even when it's irrelevant. Both are cognitive traps that destroy investment returns.",
          visual: "biases",
          analogy: {
            icon: "🔍", title: "The Biased Detective",
            text: "Imagine a detective who decides the butler is guilty, then only looks for clues that confirm it — ignoring all evidence pointing elsewhere. That's confirmation bias. Anchoring is like a shopkeeper who insists his product is worth ₹500 because he bought it for ₹500, even when the market price is ₹200."
          },
          keyPoints: [
            "Confirmation bias: seek views that CHALLENGE your thesis",
            "Anchoring: the price you paid is IRRELEVANT to future returns",
            "Read bear cases for stocks you're bullish on",
            "'I can't sell at a loss' = dangerous anchoring trap"
          ]
        },
        quiz: {
          question: "You bought Xyz stock at ₹200. It's now at ₹100. You refuse to sell 'because it might go back to ₹200'. This is:",
          options: ["Good long-term thinking", "Anchoring bias — the ₹200 price is irrelevant now", "Value investing", "Correct — always wait for recovery"],
          correct: 1,
          explanation: "Classic anchoring bias. Whether you paid ₹200 or ₹50 is irrelevant — the question is whether the stock will go UP FROM ₹100. Refusing to cut losses because of 'the price I paid' has destroyed many portfolios. The market doesn't know or care what you paid."
        }
      },
      {
        id: "l17-3", title: "Patience — The Rarest Edge",
        duration: "2 min", type: "concept",
        content: {
          headline: "The best investors are the most boring ones",
          body: "Studies show the best-performing retail accounts are often those whose owners forgot they had them (or died). Less trading = fewer mistakes + more compounding. Warren Buffett held Coca-Cola for 35+ years. The biggest wealth multiplier is patient inaction.",
          visual: "patience",
          analogy: {
            icon: "🌳", title: "The Farmer's Wisdom",
            text: "A farmer plants seeds, then waits months without digging them up to 'check progress'. Digging up seeds every week kills the crop. Investors who check their portfolio hourly and trade frequently are digging up their seeds. Plant quality stocks in quality companies, then let time do the work."
          },
          keyPoints: [
            "Ideal holding period for quality stocks: 3–10+ years",
            "Every transaction has costs — minimize unnecessary trading",
            "Time in market beats timing the market",
            "Set up SIP, forget it, check quarterly — not daily"
          ]
        },
        quiz: {
          question: "Investor A checks their portfolio daily and trades frequently. Investor B invests monthly via SIP and reviews quarterly. Over 15 years, who likely makes more?",
          options: ["Investor A — more active = more opportunities", "Investor B — low cost, compounding works uninterrupted", "Both equal", "Depends on market conditions"],
          correct: 1,
          explanation: "Studies consistently show passive, low-frequency investors outperform active traders. Transaction costs, taxes, emotional mistakes, and missed compounding all erode Investor A's returns. Investor B's disciplined SIP approach allows compounding to work its full magic over 15 years."
        }
      }
    ]
  },
  {
    id: 18, title: "Professional Stock Analysis", icon: "🎯", xp: 300,
    color: "#22c55e", description: "The full 8-step workflow professionals use",
    lessons: [
      {
        id: "l18-1", title: "Step 1–4: Business, Industry, Management, Financials",
        duration: "5 min", type: "framework",
        content: {
          headline: "The foundation of every great investment",
          body: "Professional stock analysis follows a systematic process. Steps 1–4 form the qualitative and quantitative foundation: understand the business deeply, assess industry dynamics, evaluate management quality, and verify financial health.",
          visual: "proanalysis",
          analogy: {
            icon: "🏗️", title: "Building a House",
            text: "You wouldn't build a house by starting with the paint color (valuation). You start with land quality (business), soil test (industry), architect quality (management), then check blueprints (financials). Only then do you estimate cost (valuation). Most beginner mistakes come from skipping the first four steps."
          },
          keyPoints: [
            "Step 1 Business: What does it sell? Who are customers? Competitive moat?",
            "Step 2 Industry: Is the sector growing? Who are rivals? Regulatory threats?",
            "Step 3 Management: Promoter track record, insider holding, governance",
            "Step 4 Financials: Revenue growth, ROE, FCF, Debt — 5-year trends"
          ]
        },
        quiz: {
          question: "An analyst skips Step 1 (business) and goes straight to checking PE ratio. What is the main problem?",
          options: ["Nothing — PE is most important", "PE is meaningless without understanding what business generates those earnings", "Should check RSI instead", "Should check promoter holding first"],
          correct: 1,
          explanation: "PE = Price/Earnings, but Earnings quality depends on the business. A cyclical company (steel, cement) may show low PE at a cycle peak — right before earnings collapse. Without understanding the business cycle, low PE looks like value but is actually a trap. Context from Step 1 transforms how you interpret every number."
        }
      },
      {
        id: "l18-2", title: "Step 5–8: Valuation, Technicals, Risk, Decision",
        duration: "5 min", type: "framework",
        content: {
          headline: "From analysis to conviction to action",
          body: "Steps 5–8 complete the framework: value the business fairly, confirm entry with technicals, stress-test the risks, then make a documented investment decision. This process takes 2–4 hours for a new stock but builds conviction and prevents emotional mistakes.",
          visual: "decision",
          analogy: {
            icon: "✈️", title: "The Pre-Flight Checklist",
            text: "Pilots don't take off on gut feel — they use a checklist: engine ✓ fuel ✓ weather ✓ instruments ✓ clearance ✓. A professional investor's checklist: valuation fair ✓ chart setup good ✓ risks manageable ✓ thesis documented ✓. Following a checklist removes emotion from the cockpit."
          },
          keyPoints: [
            "Step 5 Valuation: Is stock cheap/fair/expensive vs earnings power?",
            "Step 6 Technicals: Use charts to time entry, reduce average cost",
            "Step 7 Risk: What are the top 3 things that could go wrong?",
            "Step 8 Decision: Write your thesis — if it changes, so does your position"
          ]
        },
        quiz: {
          question: "You've done thorough analysis on Titan Company. It's a great business with good management and fair valuation. But the chart shows it just fell 15% from all-time highs. What's the smart move?",
          options: ["Avoid — falling knife!", "Excellent — buy immediately without waiting", "Use technicals to find a low-risk entry (support level, high-volume bounce)", "Wait for it to recover to all-time highs"],
          correct: 2,
          explanation: "This is the integration of fundamental (great business) and technical (timing entry) analysis. A quality stock in a pullback is an opportunity — but entry matters for returns. Look for a support level, wait for stabilization, buy on strength with high volume. This is how professionals buy quality dips."
        }
      }
    ]
  },
  {
    id: 19, title: "Using VeeraStockIQ", icon: "🤖", xp: 150,
    color: "#22c55e", description: "Master every tool on this platform",
    lessons: [
      {
        id: "l19-1", title: "AI Signals — What They Mean",
        duration: "3 min", type: "concept",
        content: {
          headline: "Your AI-powered research assistant",
          body: "VeeraStockIQ's AI Signals analyze technical, fundamental, and news data simultaneously to generate insights. They are NOT buy/sell recommendations — they are research starting points. Always apply your own judgment on top of any AI output.",
          visual: "aisignals",
          analogy: {
            icon: "🤖", title: "The Research Intern",
            text: "AI Signals are like a very fast, tireless research intern who reads 1000 data points in seconds. The intern brings you a report — but you, the senior analyst, make the final call. Use AI signals to shortlist stocks for deeper research, not as the sole reason to buy or sell."
          },
          keyPoints: [
            "AI Signals combine technical + fundamental + news data",
            "Bullish signal = multiple indicators align positively",
            "Always read the signal's reasoning, not just the verdict",
            "Combine with your own analysis for best results"
          ]
        },
        quiz: {
          question: "VeeraStockIQ AI gives a Bullish signal on a company. What should you do next?",
          options: ["Buy immediately — AI is always right", "Use it as a research starting point and verify yourself", "Ignore it — AI can't predict markets", "Short-sell the stock to take the contrarian view"],
          correct: 1,
          explanation: "AI signals are research tools, not oracles. Use the bullish signal to trigger deeper investigation: check the fundamental analysis tab, look at the chart, read recent news. AI helps you work faster, but your judgment is the final filter. Never outsource thinking completely to any algorithm."
        }
      },
      {
        id: "l19-2", title: "Building Your Watchlist & Portfolio",
        duration: "3 min", type: "concept",
        content: {
          headline: "Turn VeeraStockIQ into your personal research command center",
          body: "The Watchlist lets you track stocks you're researching but haven't bought yet. The Portfolio tracks your actual holdings. Use them together: research in watchlist → conviction → buy → move to portfolio. Review both weekly.",
          visual: "platform",
          analogy: {
            icon: "🎯", title: "The Shortlist and the Team",
            text: "A cricket selector has a watchlist of 50 promising players they're tracking. After trials, 11 make the playing XI (portfolio). VeeraStockIQ works identically. Your watchlist is your talent pipeline. Only the best researched, highest-conviction ideas make it to your actual portfolio."
          },
          keyPoints: [
            "Watchlist: track 20–30 stocks you've researched",
            "Portfolio: only hold 10–15 high-conviction positions maximum",
            "Set price alerts on watchlist stocks for entry opportunities",
            "Review portfolio performance quarterly, not daily"
          ]
        },
        quiz: {
          question: "A good portfolio for a beginner investor should have:",
          options: ["50–100 stocks for maximum diversification", "1–2 stocks for focus and conviction", "10–15 well-researched stocks across sectors", "Only FDs and gold — avoid stocks"],
          correct: 2,
          explanation: "10–15 stocks provides real diversification without over-diversification. Below 10, one bad stock hurts significantly. Above 20–25, you can't track all of them properly. Across sectors means a crisis in one industry (say banking in 2019) doesn't wipe out your entire portfolio."
        }
      }
    ]
  }
];

const BADGES = [
  { id: "first_lesson", icon: "🌱", name: "First Step", desc: "Complete your first lesson", xp: 50 },
  { id: "level_1", icon: "🏛️", name: "Market Initiate", desc: "Complete Level 1", xp: 150 },
  { id: "level_2", icon: "🏦", name: "Demat Pro", desc: "Complete Level 2", xp: 150 },
  { id: "level_3", icon: "📚", name: "Term Master", desc: "Complete Level 3", xp: 200 },
  { id: "level_4", icon: "🔬", name: "Fundamentals Ace", desc: "Complete Level 4", xp: 250 },
  { id: "level_5", icon: "📈", name: "Chart Reader", desc: "Complete Level 5", xp: 250 },
  { id: "quiz_3", icon: "🧠", name: "Quiz Streak x3", desc: "Get 3 correct in a row", xp: 100 },
  { id: "perfect_score", icon: "💯", name: "Perfect Score", desc: "Ace 5 quizzes in a row", xp: 200 },
  { id: "streak_7", icon: "🔥", name: "Week Warrior", desc: "7-day learning streak", xp: 300 },
  { id: "halfway", icon: "🏅", name: "Halfway Hero", desc: "Complete 4 levels", xp: 500 },
];

const FLASHCARDS = [
  { term: "SEBI", def: "Securities and Exchange Board of India — the regulator that protects investors and oversees the stock market. Think of it as the 'police' of the stock market." },
  { term: "IPO", def: "Initial Public Offering — the first time a company sells its shares to the public. Like the company's 'debut' in the stock market." },
  { term: "FII", def: "Foreign Institutional Investors — big foreign funds like BlackRock that invest in Indian markets. When FIIs buy, markets tend to go up." },
  { term: "DII", def: "Domestic Institutional Investors — Indian institutions like LIC, mutual funds that invest locally. They often buy when FIIs are selling." },
  { term: "Circuit Breaker", def: "Automatic trading halt when a stock moves ±5%, ±10%, or ±20% in a day. Prevents panic buying/selling. Like a fuse for the market." },
  { term: "EPS", def: "Earnings Per Share = Net Profit ÷ Total Shares. If a company earns ₹100 crore and has 10 crore shares, EPS = ₹10." },
  { term: "Book Value", def: "What a company is worth on paper = Total Assets − Total Liabilities. If a stock trades BELOW book value, it may be undervalued." },
  { term: "ROCE", def: "Return on Capital Employed — how efficiently a company uses its capital. ROCE > 15% is generally considered good for Indian companies." },
  { term: "Promoter Holding", def: "The percentage of company shares held by the founders/promoters. High promoter holding (60%+) shows founders have skin in the game." },
  { term: "52-Week High/Low", def: "The highest and lowest price a stock has traded in the past year. Stocks near 52-week highs can mean momentum; near lows can mean value opportunity." },
  { term: "MACD", def: "Moving Average Convergence Divergence — a technical indicator showing momentum. When MACD crosses above its signal line, it's a bullish signal." },
  { term: "RSI", def: "Relative Strength Index — measures if a stock is overbought (RSI > 70) or oversold (RSI < 30). A key momentum indicator." },
];

// ── Helper hooks ──────────────────────────────────────────────────────────────
function useProgress() {
  const [progress, setProgress] = useState(() => {
    try {
      const saved = localStorage.getItem("vsiq_learn_progress");
      return saved ? JSON.parse(saved) : {
        completedLessons: [],
        earnedBadges: [],
        xp: 0,
        streak: 0,
        lastActive: null,
        quizCorrectStreak: 0,
      };
    } catch { return { completedLessons: [], earnedBadges: [], xp: 0, streak: 0, lastActive: null, quizCorrectStreak: 0 }; }
  });

  const save = useCallback((p) => {
    setProgress(p);
    try { localStorage.setItem("vsiq_learn_progress", JSON.stringify(p)); } catch {}
  }, []);

  const completeLesson = useCallback((lessonId, levelId, xpGain) => {
    setProgress(prev => {
      if (prev.completedLessons.includes(lessonId)) return prev;
      const next = {
        ...prev,
        completedLessons: [...prev.completedLessons, lessonId],
        xp: prev.xp + xpGain,
        lastActive: new Date().toDateString(),
      };
      const newBadges = [...prev.earnedBadges];
      if (!newBadges.includes("first_lesson")) newBadges.push("first_lesson");
      const level = LEVELS.find(l => l.id === levelId);
      if (level) {
        const allDone = level.lessons.every(l => next.completedLessons.includes(l.id));
        const badgeId = `level_${levelId}`;
        if (allDone && !newBadges.includes(badgeId)) newBadges.push(badgeId);
      }
      const completedCount = LEVELS.filter(l => l.lessons.every(ls => next.completedLessons.includes(ls.id))).length;
      if (completedCount >= 4 && !newBadges.includes("halfway")) newBadges.push("halfway");
      const result = { ...next, earnedBadges: newBadges };
      try { localStorage.setItem("vsiq_learn_progress", JSON.stringify(result)); } catch {}
      return result;
    });
  }, []);

  const recordQuizResult = useCallback((correct) => {
    setProgress(prev => {
      const newStreak = correct ? prev.quizCorrectStreak + 1 : 0;
      const newBadges = [...prev.earnedBadges];
      if (newStreak >= 3 && !newBadges.includes("quiz_3")) newBadges.push("quiz_3");
      if (newStreak >= 5 && !newBadges.includes("perfect_score")) newBadges.push("perfect_score");
      const result = { ...prev, quizCorrectStreak: newStreak, earnedBadges: newBadges };
      try { localStorage.setItem("vsiq_learn_progress", JSON.stringify(result)); } catch {}
      return result;
    });
  }, []);

  return { progress, completeLesson, recordQuizResult };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function XPBar({ xp }) {
  const maxXP = 1750;
  const pct = Math.min((xp / maxXP) * 100, 100);
  const level = xp < 100 ? 1 : xp < 300 ? 2 : xp < 600 ? 3 : xp < 1000 ? 4 : xp < 1500 ? 5 : 6;
  return (
    <div className="lrn-xpbar">
      <div className="lrn-xpbar-info">
        <span className="lrn-xp-level">Lv {level}</span>
        <span className="lrn-xp-val">{xp} XP</span>
      </div>
      <div className="lrn-xpbar-track">
        <div className="lrn-xpbar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="lrn-xp-next">{maxXP - xp} XP to max</span>
    </div>
  );
}

function BadgeRow({ badges }) {
  const earned = BADGES.filter(b => badges.includes(b.id));
  if (earned.length === 0) return null;
  return (
    <div className="lrn-badge-row">
      {earned.map(b => (
        <div key={b.id} className="lrn-badge" title={`${b.name}: ${b.desc}`}>
          <span className="lrn-badge-icon">{b.icon}</span>
          <span className="lrn-badge-name">{b.name}</span>
        </div>
      ))}
    </div>
  );
}

function StatsRow({ progress }) {
  const totalLessons = LEVELS.reduce((a, l) => a + l.lessons.length, 0);
  const completedCount = progress.completedLessons.length;
  const pct = Math.round((completedCount / totalLessons) * 100);
  return (
    <div className="lrn-stats-row">
      <div className="lrn-stat-chip">
        <span className="lrn-stat-num">{progress.xp}</span>
        <span className="lrn-stat-lbl">Total XP</span>
      </div>
      <div className="lrn-stat-chip">
        <span className="lrn-stat-num">{completedCount}</span>
        <span className="lrn-stat-lbl">Lessons Done</span>
      </div>
      <div className="lrn-stat-chip">
        <span className="lrn-stat-num">{pct}%</span>
        <span className="lrn-stat-lbl">Complete</span>
      </div>
      <div className="lrn-stat-chip">
        <span className="lrn-stat-num">{progress.earnedBadges.length}</span>
        <span className="lrn-stat-lbl">Badges</span>
      </div>
    </div>
  );
}

// Visual helpers for lesson content
function LessonVisual({ type }) {
  if (type === "bazaar") return (
    <div className="lrn-visual-box">
      <div className="lrn-visual-market">
        {["🏢 Reliance", "💻 TCS", "🏦 HDFC", "🛢️ ONGC"].map((c, i) => (
          <div key={i} className="lrn-market-chip">{c}</div>
        ))}
        <div className="lrn-market-arrow">⬇️ Shares for sale</div>
        <div className="lrn-market-buyers">
          {["👤 You", "👤 Investor 2", "👤 Investor 3"].map((u, i) => (
            <div key={i} className="lrn-buyer-chip">{u}</div>
          ))}
        </div>
      </div>
    </div>
  );
  if (type === "candle") return (
    <div className="lrn-visual-box">
      <div className="lrn-candle-demo">
        <div className="lrn-candle-labels">
          <span>High ₹1,550</span>
          <span>Close ₹1,530</span>
          <span>Open ₹1,500</span>
          <span>Low ₹1,480</span>
        </div>
        <div className="lrn-candle-chart">
          <div className="lrn-candle-wick-top" />
          <div className="lrn-candle-body green" />
          <div className="lrn-candle-wick-bot" />
        </div>
      </div>
    </div>
  );
  if (type === "bullbear") return (
    <div className="lrn-visual-box">
      <div className="lrn-bb-grid">
        <div className="lrn-bb-card bull">
          <div className="lrn-bb-emoji">🐂</div>
          <div className="lrn-bb-title">Bull Market</div>
          <div className="lrn-bb-desc">Prices rising 20%+</div>
          <div className="lrn-bb-trend">📈 Optimistic</div>
        </div>
        <div className="lrn-bb-card bear">
          <div className="lrn-bb-emoji">🐻</div>
          <div className="lrn-bb-title">Bear Market</div>
          <div className="lrn-bb-desc">Prices falling 20%+</div>
          <div className="lrn-bb-trend">📉 Pessimistic</div>
        </div>
      </div>
    </div>
  );
  if (type === "pe") return (
    <div className="lrn-visual-box">
      <div className="lrn-pe-scale">
        <div className="lrn-pe-zone low">PE &lt; 10<br/><small>Possibly cheap</small></div>
        <div className="lrn-pe-zone mid">PE 10–25<br/><small>Fair value</small></div>
        <div className="lrn-pe-zone high">PE &gt; 40<br/><small>Growth priced in</small></div>
      </div>
    </div>
  );
  if (type === "sr") return (
    <div className="lrn-visual-box">
      <div className="lrn-sr-chart">
        <div className="lrn-sr-resistance">🔴 Resistance — ₹2,400 (sellers dominate here)</div>
        <div className="lrn-sr-price-zone">
          <span className="lrn-sr-bounce">↗ bounce ↘ bounce ↗ BREAKOUT!</span>
        </div>
        <div className="lrn-sr-support">🟢 Support — ₹2,200 (buyers step in here)</div>
      </div>
    </div>
  );
  // Default visual
  return (
    <div className="lrn-visual-box lrn-visual-default">
      <span className="lrn-visual-icon">💡</span>
      <span className="lrn-visual-hint">Concept</span>
    </div>
  );
}

function QuizBlock({ quiz, onResult }) {
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);

  function handleSelect(idx) {
    if (revealed) return;
    setSelected(idx);
    setRevealed(true);
    onResult(idx === quiz.correct);
  }

  return (
    <div className="lrn-quiz">
      <div className="lrn-quiz-label">🧠 Quick Check</div>
      <div className="lrn-quiz-q">{quiz.question}</div>
      <div className="lrn-quiz-opts">
        {quiz.options.map((opt, i) => {
          let cls = "lrn-quiz-opt";
          if (revealed) {
            if (i === quiz.correct) cls += " correct";
            else if (i === selected) cls += " wrong";
          }
          return (
            <button key={i} className={cls} onClick={() => handleSelect(i)}>
              <span className="lrn-quiz-letter">{["A","B","C","D"][i]}</span>
              {opt}
            </button>
          );
        })}
      </div>
      {revealed && (
        <div className={`lrn-quiz-feedback ${selected === quiz.correct ? "correct" : "wrong"}`}>
          <span>{selected === quiz.correct ? "✅ Correct!" : "❌ Not quite."}</span>
          <p>{quiz.explanation}</p>
        </div>
      )}
    </div>
  );
}

function LessonModal({ lesson, level, onClose, onComplete, recordQuiz }) {
  const [page, setPage] = useState(0); // 0 = content, 1 = quiz, 2 = done
  const [quizDone, setQuizDone] = useState(false);

  function handleQuizResult(correct) {
    recordQuiz(correct);
    setQuizDone(true);
    setTimeout(() => setPage(2), 1200);
  }

  function handleFinish() {
    onComplete(lesson.id, level.id, Math.floor(level.xp / level.lessons.length));
    onClose();
  }

  return (
    <div className="lrn-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lrn-modal">
        <div className="lrn-modal-header">
          <div className="lrn-modal-meta">
            <span className="lrn-modal-level" style={{ color: level.color }}>{level.icon} {level.title}</span>
            <span className="lrn-modal-duration">⏱ {lesson.duration}</span>
          </div>
          <button className="lrn-modal-close" onClick={onClose}>✕</button>
        </div>

        {page === 0 && (
          <div className="lrn-modal-content">
            <h2 className="lrn-lesson-headline">{lesson.content.headline}</h2>
            <LessonVisual type={lesson.content.visual} />
            <p className="lrn-lesson-body">{lesson.content.body}</p>
            {lesson.content.analogy && (
              <div className="lrn-analogy">
                <div className="lrn-analogy-icon">{lesson.content.analogy.icon}</div>
                <div>
                  <div className="lrn-analogy-title">{lesson.content.analogy.title}</div>
                  <div className="lrn-analogy-text">{lesson.content.analogy.text}</div>
                </div>
              </div>
            )}
            {lesson.content.keyPoints && (
              <div className="lrn-keypoints">
                <div className="lrn-kp-label">Key Takeaways</div>
                {lesson.content.keyPoints.map((kp, i) => (
                  <div key={i} className="lrn-kp-item">
                    <span className="lrn-kp-dot" />
                    {kp}
                  </div>
                ))}
              </div>
            )}
            <button className="lrn-btn-primary" onClick={() => setPage(1)}>
              Take the Quiz →
            </button>
          </div>
        )}

        {page === 1 && (
          <div className="lrn-modal-content">
            <QuizBlock quiz={lesson.quiz} onResult={handleQuizResult} />
          </div>
        )}

        {page === 2 && (
          <div className="lrn-modal-content lrn-complete-screen">
            <div className="lrn-complete-star">⭐</div>
            <h2>Lesson Complete!</h2>
            <div className="lrn-complete-xp">+{Math.floor(level.xp / level.lessons.length)} XP earned</div>
            <p className="lrn-complete-msg">You're building real knowledge. Keep going!</p>
            <button className="lrn-btn-primary" onClick={handleFinish}>Continue Learning →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function FlashcardDeck() {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState([]);
  const [reviewing, setReviewing] = useState(false);

  const remaining = FLASHCARDS.filter((_, i) => !known.includes(i));
  const current = reviewing ? remaining[idx % Math.max(remaining.length, 1)] : FLASHCARDS[idx];

  function handleKnow() {
    if (!reviewing) {
      setKnown(prev => [...prev, idx]);
    } else {
      const ri = idx % remaining.length;
      const originalIdx = FLASHCARDS.indexOf(remaining[ri]);
      setKnown(prev => [...prev, originalIdx]);
    }
    setFlipped(false);
    setIdx(p => p + 1);
  }

  function handleNext() {
    setFlipped(false);
    setIdx(p => p + 1);
  }

  const displayIdx = reviewing ? (idx % Math.max(remaining.length, 1)) : (idx % FLASHCARDS.length);
  const card = reviewing ? remaining[displayIdx] : FLASHCARDS[idx % FLASHCARDS.length];

  return (
    <div className="lrn-flashcard-section">
      <div className="lrn-fc-header">
        <h3 className="lrn-fc-title">📇 Flashcards</h3>
        <div className="lrn-fc-progress">{known.length}/{FLASHCARDS.length} mastered</div>
      </div>
      <div className="lrn-fc-deck" onClick={() => setFlipped(f => !f)}>
        <div className={`lrn-fc-card ${flipped ? "flipped" : ""}`}>
          <div className="lrn-fc-front">
            <div className="lrn-fc-term">{card.term}</div>
            <div className="lrn-fc-hint">Tap to reveal definition</div>
          </div>
          <div className="lrn-fc-back">
            <div className="lrn-fc-def">{card.def}</div>
          </div>
        </div>
      </div>
      <div className="lrn-fc-actions">
        <button className="lrn-fc-btn skip" onClick={handleNext}>Skip →</button>
        <button className="lrn-fc-btn know" onClick={handleKnow}>Got it ✓</button>
      </div>
      <div className="lrn-fc-bar">
        <div className="lrn-fc-bar-fill" style={{ width: `${(known.length / FLASHCARDS.length) * 100}%` }} />
      </div>
    </div>
  );
}

function LevelCard({ level, progress, onStartLesson, isLocked }) {
  const completedInLevel = level.lessons.filter(l => progress.completedLessons.includes(l.id)).length;
  const allDone = completedInLevel === level.lessons.length;
  const pct = Math.round((completedInLevel / level.lessons.length) * 100);

  return (
    <div className={`lrn-level-card ${isLocked ? "locked" : ""} ${allDone ? "done" : ""}`}>
      <div className="lrn-level-header">
        <div className="lrn-level-icon" style={{ background: isLocked ? "#1e2d45" : `${level.color}22`, border: `1px solid ${isLocked ? "#1e2d45" : level.color}44` }}>
          {isLocked ? "🔒" : level.icon}
        </div>
        <div className="lrn-level-info">
          <div className="lrn-level-num" style={{ color: isLocked ? "#4a5568" : level.color }}>Level {level.id}</div>
          <div className="lrn-level-title">{level.title}</div>
          <div className="lrn-level-desc">{level.description}</div>
        </div>
        <div className="lrn-level-badge">
          {allDone ? <span className="lrn-done-badge">✓ Done</span> :
           isLocked ? <span className="lrn-locked-badge">Locked</span> :
           <span className="lrn-xp-badge">+{level.xp} XP</span>}
        </div>
      </div>

      {!isLocked && (
        <>
          <div className="lrn-level-progress-bar">
            <div className="lrn-level-progress-fill" style={{ width: `${pct}%`, background: level.color }} />
          </div>
          <div className="lrn-level-lessons">
            {level.lessons.map(lesson => {
              const done = progress.completedLessons.includes(lesson.id);
              return (
                <button
                  key={lesson.id}
                  className={`lrn-lesson-btn ${done ? "done" : ""}`}
                  onClick={() => !done && onStartLesson(lesson, level)}
                  style={{ borderColor: done ? level.color + "66" : "#1e2d45" }}
                >
                  <span className="lrn-lesson-status">{done ? "✅" : "▶"}</span>
                  <span className="lrn-lesson-name">{lesson.title}</span>
                  <span className="lrn-lesson-dur">{lesson.duration}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function LearnPage() {
  const { progress, completeLesson, recordQuizResult } = useProgress();
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeLevel, setActiveLevel] = useState(null);
  const [activeTab, setActiveTab] = useState("path");
  const [newBadge, setNewBadge] = useState(null);
  const [prevBadges, setPrevBadges] = useState(progress.earnedBadges);

  useEffect(() => {
    const added = progress.earnedBadges.filter(b => !prevBadges.includes(b));
    if (added.length > 0) {
      const badge = BADGES.find(b => b.id === added[added.length - 1]);
      if (badge) {
        setNewBadge(badge);
        setTimeout(() => setNewBadge(null), 3500);
      }
    }
    setPrevBadges(progress.earnedBadges);
  }, [progress.earnedBadges]);

  function handleStartLesson(lesson, level) {
    setActiveLesson(lesson);
    setActiveLevel(level);
  }

  function handleCloseLesson() {
    setActiveLesson(null);
    setActiveLevel(null);
  }

  function isLevelLocked(level) {
    if (level.id === 1) return false;
    const prevLevel = LEVELS.find(l => l.id === level.id - 1);
    if (!prevLevel) return false;
    return !prevLevel.lessons.every(l => progress.completedLessons.includes(l.id));
  }

  const completedLevels = LEVELS.filter(l => l.lessons.every(ls => progress.completedLessons.includes(ls.id))).length;
  const totalLessons = LEVELS.reduce((a, l) => a + l.lessons.length, 0);
  const overallPct = Math.round((progress.completedLessons.length / totalLessons) * 100);

  return (
    <div className="lrn-page">

      {/* Badge Toast */}
      {newBadge && (
        <div className="lrn-badge-toast">
          <span className="lrn-toast-icon">{newBadge.icon}</span>
          <div>
            <div className="lrn-toast-title">Badge Unlocked!</div>
            <div className="lrn-toast-name">{newBadge.name}</div>
          </div>
          <div className="lrn-toast-xp">+{newBadge.xp} XP</div>
        </div>
      )}

      {/* Hero */}
      <div className="lrn-hero">
        <div className="lrn-hero-bg">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="lrn-hero-dot" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }} />
          ))}
        </div>
        <div className="lrn-hero-content">
          <div className="lrn-hero-eyebrow">📚 Learn Stock Market</div>
          <h1 className="lrn-hero-title">
            From Zero to <span className="lrn-hero-accent">Investor Ready</span>
          </h1>
          <p className="lrn-hero-sub">
            Bite-sized lessons. Real analogies. Interactive quizzes.<br/>
            Built for complete beginners. No jargon. No boring PDFs.
          </p>
          <div className="lrn-hero-chips">
            <span className="lrn-chip">⚡ 2–5 min lessons</span>
            <span className="lrn-chip">🎯 Quizzes after every lesson</span>
            <span className="lrn-chip">🏅 Earn XP & badges</span>
            <span className="lrn-chip">🆓 100% free</span>
          </div>
        </div>

        <div className="lrn-hero-stats">
          <XPBar xp={progress.xp} />
          <StatsRow progress={progress} />
          <div className="lrn-hero-overall">
            <div className="lrn-overall-label">Overall Progress — {overallPct}%</div>
            <div className="lrn-overall-bar">
              <div className="lrn-overall-fill" style={{ width: `${overallPct}%` }} />
            </div>
            <div className="lrn-overall-sub">{completedLevels} of {LEVELS.length} levels complete</div>
          </div>
          {progress.earnedBadges.length > 0 && <BadgeRow badges={progress.earnedBadges} />}
        </div>
      </div>

      {/* Tabs */}
      <div className="lrn-tabs">
        <button className={`lrn-tab ${activeTab === "path" ? "active" : ""}`} onClick={() => setActiveTab("path")}>🗺️ Learning Path</button>
        <button className={`lrn-tab ${activeTab === "cards" ? "active" : ""}`} onClick={() => setActiveTab("cards")}>📇 Flashcards</button>
        <button className={`lrn-tab ${activeTab === "badges" ? "active" : ""}`} onClick={() => setActiveTab("badges")}>🏅 Badges</button>
      </div>

      <div className="lrn-body">

        {activeTab === "path" && (
          <div className="lrn-path">
            <div className="lrn-path-intro">
              <p>Complete lessons in order to unlock the next level. Each lesson takes just 2–5 minutes.</p>
            </div>
            {LEVELS.map(level => (
              <LevelCard
                key={level.id}
                level={level}
                progress={progress}
                onStartLesson={handleStartLesson}
                isLocked={isLevelLocked(level)}
              />
            ))}
          </div>
        )}

        {activeTab === "cards" && <FlashcardDeck />}

        {activeTab === "badges" && (
          <div className="lrn-badges-page">
            <p className="lrn-badges-intro">Earn badges by completing levels and quizzes. {BADGES.length - progress.earnedBadges.length} badges still locked.</p>
            <div className="lrn-badges-grid">
              {BADGES.map(badge => {
                const earned = progress.earnedBadges.includes(badge.id);
                return (
                  <div key={badge.id} className={`lrn-badge-card ${earned ? "earned" : "unearned"}`}>
                    <div className="lrn-bc-icon">{earned ? badge.icon : "🔒"}</div>
                    <div className="lrn-bc-name">{badge.name}</div>
                    <div className="lrn-bc-desc">{badge.desc}</div>
                    {earned && <div className="lrn-bc-xp">+{badge.xp} XP</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Lesson Modal */}
      {activeLesson && activeLevel && (
        <LessonModal
          lesson={activeLesson}
          level={activeLevel}
          onClose={handleCloseLesson}
          onComplete={completeLesson}
          recordQuiz={recordQuizResult}
        />
      )}
    </div>
  );
}